import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Collapse, Box, Typography, Grid, Button, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey } from '@mui/material/colors';
import format from 'date-fns/format';

import { sendMessageToSales } from 'modules/shop/services';
import { Event, Document } from 'modules/events/types/eventsTypes';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import { fetchEventDetailsAction } from 'modules/events/store/eventsActions';
import { getEventsDetails } from 'modules/events/selectors/eventsSelectors';
import { sanitizeHtml } from 'shared/functions';
import { SummaryCardDocuments } from 'modules/common/SummaryCard';
import { FlagsList } from 'ui';
import { MessageModal } from 'ui/Modals';
import { eventsCategories } from 'modules/events/constants/eventsCategories';
import { EventsActionButton } from '../EventsActionButton';

const useStyle = makeStyles(theme => ({
  stats: {
    textAlign: 'center',
  },
  statValue: {
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontWeight: 'bold',
  },
  footer: {
    borderWidth: '5px 0 0 0',
    borderStyle: 'solid',
    borderColor: grey[300],
  },
  checkoutButton: {
    backgroundColor: '#ff8900',
    width: 175,
    height: 45,
    marginLeft: 15,
  },
  button: {
    height: 45,
  },
  price: {
    fontWeight: 500,
  },
  sidebar: {
    borderWidth: '0 0 0 3px',
    borderStyle: 'solid',
    borderColor: grey[300],
  },
  container: {
    marginTop: theme.spacing(4),
  },
  subtitle: {
    color: theme.palette.primary.main,
  },
  divider: {
    marginTop: 5,
    backgroundColor: theme.palette.text.primary,
  },
  detailsInfo: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  detailsInfoList: {
    textAlign: 'right',
  },
  description: {
    fontSize: 16,
  },
  tooltip: {
    position: 'relative',
    top: 2,
    marginRight: 5,
  },
  documents: {
    '& p': {
      fontWeight: 500,
      fontSize: 16,
    },
    '& button': {
      fontSize: 16,
      color: theme.palette.primary.main,
    },
  },
  subscriptionButton: {
    height: 45,
    marginLeft: 10,
  },
}));

type EventsProductDetailsProps = {
  product: Event;
  expanded?: boolean;
  preview?: boolean;
};

const EventsProductDetails: FC<EventsProductDetailsProps> = ({ product, expanded, preview }) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const toggleMessageModal = () => setMessageModalOpen(!messageModalOpen);
  const eventDetails = useSelector(getEventsDetails);

  const { description, documentList } = eventDetails[product.id as string]?.data || {};

  const documents = documentList
    ? (documentList as Document[]).map(({ id, title }) => ({
        title: title.split('.')[0],
        path: `/storefront/document/LIVESTORM_EVENT/${id}/`,
        name: title,
      }))
    : [];

  const sendMessage = (messageData: string) => {
    sendMessageToSales(messageData)
      .then(() => {
        dispatch(
          addNotificationAction({
            message: t('Message sent successfully'),
            variant: 'success',
          }),
        );
      })
      .catch(() => {
        dispatch(
          addNotificationAction({
            message: t('Failed to send a message'),
            variant: 'error',
          }),
        );
      });
  };

  useEffect(() => {
    dispatch(fetchEventDetailsAction(product.id as string));
  }, [product.id, dispatch]);

  return (
    <Collapse in={expanded || preview} timeout="auto" unmountOnExit>
      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={8}>
          <Box mb={4}>
            <Typography variant="h6">
              <strong>Event overview</strong>
            </Typography>
            <Typography>{sanitizeHtml(description || product.description)}</Typography>
          </Box>
        </Grid>
        <Grid item xs={4} className={classes.sidebar}>
          <Box px={4}>
            <Typography variant="h6" className={classes.subtitle}>
              {t('Event details')}
            </Typography>
            <Divider className={classes.divider} />
            <Box mt={2}>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>{t('Category:')}</Grid>
                <Grid item className={classes.detailsInfoList}>
                  <Box>{eventsCategories[product.category]}</Box>
                </Grid>
              </Grid>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>{t('Start time:')}</Grid>
                <Grid item className={classes.detailsInfoList}>
                  <Box>{format(new Date(product.eventDate), 'K.mm bbbb')}</Box>
                </Grid>
              </Grid>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>{t('Duration:')}</Grid>
                <Grid item className={classes.detailsInfoList}>
                  <Box>{product.duration} minutes</Box>
                </Grid>
              </Grid>
              {product.languages && (
                <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                  <Grid item>{t('Language:')}</Grid>
                  <Grid item className={classes.detailsInfoList}>
                    <FlagsList
                      countryCodes={[product.languages === 'en' ? 'gb' : product.languages]}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>{t('Organizer:')}</Grid>
                <Grid item className={classes.detailsInfoList}>
                  <Box>{product.organiser}</Box>
                </Grid>
              </Grid>
              <Grid container justifyContent="space-between" className={classes.detailsInfo}>
                <Grid item>{t('Location:')}</Grid>
                <Grid item className={classes.detailsInfoList}>
                  <Box>{product.location.name}</Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box px={4}>
            <Typography variant="h6" className={classes.subtitle}>
              {t('Documents')}
            </Typography>
            <Divider className={classes.divider} />
            <Box mt={2}>
              <SummaryCardDocuments
                documentTypes={documents}
                className={classes.documents}
                disabled={preview}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Box p={4} mt={4} className={classes.footer}>
        <Grid container justifyContent="flex-end" alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={toggleMessageModal}
              disabled={preview}
            >
              {t('Contact Sales')}
            </Button>
            <EventsActionButton
              event={product}
              className={classes.checkoutButton}
              disabled={preview}
              detailsView
            />
          </Grid>
        </Grid>
      </Box>
      <MessageModal
        title={t('Contact Sales')}
        open={messageModalOpen}
        onClose={toggleMessageModal}
        onSendMessage={sendMessage}
      />
    </Collapse>
  );
};

export { EventsProductDetails };
